import { PROFILES } from '../../../assets/profiles/profiles';
import { PROD_CLIENT_LIST } from '../../interfaces/ClientGlobalInterface.type';
import { MapSection } from './chatbot.types';

/**
 * Profile groups — mirror the expectedRole arrays defined in routes.module.ts.
 * Used to avoid repetition across section declarations.
 */

// Standard ARSON, client, entity, and agrupation roles; excludes telemedida, installer, and subscriber roles.
const OPERATORS: ReadonlyArray<string> = [
  PROFILES.ARSON,
  PROFILES.USUARIO_ARSON,
  PROFILES.ADMIN_CLIENTE,
  PROFILES.USUARIO_CLIENTE,
  PROFILES.ADMIN_ENTIDAD,
  PROFILES.USUARIO_ENTIDAD,
  PROFILES.ADMIN_AGRUPACION,
  PROFILES.USUARIO_AGRUPACION,
];

// Operators + telemedida roles.
const WITH_TELEMEDIDA: ReadonlyArray<string> = [
  ...OPERATORS,
  PROFILES.TELEMEDIDA,
  PROFILES.USUARIO_TELEMEDIDA,
];

// ARSON profiles only.
const ARSON_ONLY: ReadonlyArray<string> = [
  PROFILES.ARSON,
  PROFILES.USUARIO_ARSON,
];

// ARSON, client-admin, and entity-admin profiles.
const ENTITY_ADMIN_UP: ReadonlyArray<string> = [
  PROFILES.ARSON,
  PROFILES.USUARIO_ARSON,
  PROFILES.ADMIN_CLIENTE,
  PROFILES.ADMIN_ENTIDAD,
];

// ARSON and client-admin profiles; excludes entity admins.
const CLIENT_ADMIN_UP: ReadonlyArray<string> = [
  PROFILES.ARSON,
  PROFILES.USUARIO_ARSON,
  PROFILES.ADMIN_CLIENTE,
];

// Telemedida-only profiles.
const TELEMEDIDA_ONLY: ReadonlyArray<string> = [
  PROFILES.TELEMEDIDA,
  PROFILES.USUARIO_TELEMEDIDA,
];

// Subscriber / end-consumer profile.
const SUBSCRIBER_ONLY: ReadonlyArray<string> = [
  PROFILES.ABONADO,
];

/**
 * Master section map for the chatbot concept map overlay.
 *
 * Each entry represents a navigable route or a non-navigable parent label.
 * `profiles` mirrors route `expectedRole` data; `clientIds` and `requiresGas`
 * preserve the corresponding navbar visibility restrictions. ChatbotService
 * applies these constraints before displaying the map.
 *
 * Order determines display order within each group.
 */
export const APP_MAP_SECTIONS: ReadonlyArray<MapSection> = [

  // ── Dashboard ──────────────────────────────────────────────────────────────
  {
    id: 'home',
    icon: 'fa-home',
    labelKey: 'chatbot.map.home.label',
    descKey:  'chatbot.map.home.desc',
    payload:  'principal',
    group:    'dashboard',
    profiles: [
      ...WITH_TELEMEDIDA,
      PROFILES.ABONADO,
      PROFILES.INSTALADOR,
      PROFILES.ADMIN_INSTALLATION,
    ],
  },
  {
    id: 'home-entidades',
    icon: 'fa-city',
    labelKey: 'chatbot.map.home-entidades.label',
    descKey:  'chatbot.map.home-entidades.desc',
    payload:  'principal-entidades',
    group:    'dashboard',
    parentId: 'home',
    profiles: CLIENT_ADMIN_UP,
  },
  {
    id: 'home-global',
    icon: 'fa-globe',
    labelKey: 'chatbot.map.home-global.label',
    descKey:  'chatbot.map.home-global.desc',
    payload:  'principal-global',
    group:    'dashboard',
    parentId: 'home',
    profiles: CLIENT_ADMIN_UP,
  },

  // ── Telecontrol ────────────────────────────────────────────────────────────
  {
    id: 'telecontrol',
    icon: 'fa-sliders-h',
    labelKey: 'chatbot.map.telecontrol.label',
    descKey:  'chatbot.map.telecontrol.desc',
    payload:  'telecontrol/alertas-activas',
    group:    'telecontrol',
    profiles: WITH_TELEMEDIDA,
  },
  {
    id: 'telecontrol-historico',
    icon: 'fa-history',
    labelKey: 'chatbot.map.telecontrol-historico.label',
    descKey:  'chatbot.map.telecontrol-historico.desc',
    payload:  'telecontrol/historico',
    group:    'telecontrol',
    parentId: 'telecontrol',
    profiles: WITH_TELEMEDIDA,
  },
  {
    id: 'telecontrol-esquema',
    icon: 'fa-project-diagram',
    labelKey: 'chatbot.map.telecontrol-esquema.label',
    descKey:  'chatbot.map.telecontrol-esquema.desc',
    payload:  'telecontrol/esquema-red',
    group:    'telecontrol',
    parentId: 'telecontrol',
    profiles: WITH_TELEMEDIDA,
  },
  {
    id: 'telecontrol-telemedida',
    icon: 'fa-tachometer-alt',
    labelKey: 'chatbot.map.telecontrol-telemedida.label',
    descKey:  'chatbot.map.telecontrol-telemedida.desc',
    payload:  'telecontrol/telemedida',
    group:    'telecontrol',
    parentId: 'telecontrol',
    profiles: [...TELEMEDIDA_ONLY, PROFILES.ARSON, PROFILES.USUARIO_ARSON],
    clientIds: [PROD_CLIENT_LIST.NORTEGAS],
  },
  {
    id: 'telecontrol-dataloggers',
    icon: 'fa-hdd',
    labelKey: 'chatbot.map.telecontrol-dataloggers.label',
    descKey:  'chatbot.map.telecontrol-dataloggers.desc',
    payload:  'telecontrol/dataloggers',
    group:    'telecontrol',
    parentId: 'telecontrol',
    profiles: WITH_TELEMEDIDA,
    clientIds: [PROD_CLIENT_LIST.NORTEGAS],
  },
  {
    id: 'telecontrol-test',
    icon: 'fa-vial',
    labelKey: 'chatbot.map.telecontrol-test.label',
    descKey:  'chatbot.map.telecontrol-test.desc',
    payload:  'telecontrol/test',
    group:    'telecontrol',
    parentId: 'telecontrol',
    profiles: [...WITH_TELEMEDIDA, PROFILES.ADMIN_INSTALLATION],
    clientIds: [PROD_CLIENT_LIST.NORTEGAS],
  },

  // ── Alarms ─────────────────────────────────────────────────────────────────
  {
    id: 'alarmas',
    icon: 'fa-bell',
    labelKey: 'chatbot.map.alarmas.label',
    descKey:  'chatbot.map.alarmas.desc',
    payload:  'alarmas/agrupacion/listado',
    group:    'alarms',
    profiles: [...WITH_TELEMEDIDA, PROFILES.ABONADO],
  },
  {
    id: 'alarmas-config',
    icon: 'fa-bell-slash',
    labelKey: 'chatbot.map.alarmas-config.label',
    descKey:  'chatbot.map.alarmas-config.desc',
    payload:  'alarmas/agrupacion/configuracion',
    group:    'alarms',
    parentId: 'alarmas',
    profiles: [
      PROFILES.ARSON,
      PROFILES.ADMIN_ENTIDAD,
      PROFILES.ADMIN_CLIENTE,
      PROFILES.ADMIN_AGRUPACION,
    ],
  },
  {
    id: 'alarmas-entidad-config',
    icon: 'fa-sitemap',
    labelKey: 'chatbot.map.alarmas-entidad-config.label',
    descKey:  'chatbot.map.alarmas-entidad-config.desc',
    payload:  'alarmas/entidad/configuracion',
    group:    'alarms',
    parentId: 'alarmas',
    profiles: [
      PROFILES.ARSON,
      PROFILES.ADMIN_ENTIDAD,
      PROFILES.ADMIN_CLIENTE,
    ],
  },
  {
    id: 'alarmas-global',
    icon: 'fa-globe',
    labelKey: 'chatbot.map.alarmas-global.label',
    descKey:  'chatbot.map.alarmas-global.desc',
    payload:  'alarmas/global/listado',
    group:    'alarms',
    parentId: 'alarmas',
    profiles: ARSON_ONLY,
  },
  {
    id: 'alarmas-personalizadas',
    icon: 'fa-sliders-h',
    labelKey: 'chatbot.map.alarmas-personalizadas.label',
    descKey:  'chatbot.map.alarmas-personalizadas.desc',
    payload:  'alarmas/personalizadas/configuracion',
    group:    'alarms',
    parentId: 'alarmas',
    profiles: ARSON_ONLY,
  },
  {
    id: 'alarmas-dispositivos',
    icon: 'fa-microchip',
    labelKey: 'chatbot.map.alarmas-dispositivos.label',
    descKey:  'chatbot.map.alarmas-dispositivos.desc',
    payload:  'alarmas/dispositivos',
    group:    'alarms',
    parentId: 'alarmas',
    profiles: ARSON_ONLY,
  },

  // ── Devices ────────────────────────────────────────────────────────────────
  {
    id: 'dispositivos',
    icon: 'fa-microchip',
    labelKey: 'chatbot.map.dispositivos.label',
    descKey:  'chatbot.map.dispositivos.desc',
    payload:  'dispositivos/listado/asignados',
    group:    'devices',
    profiles: [
      ...OPERATORS,
      PROFILES.ADMIN_INSTALLATION,
      PROFILES.TELEMEDIDA,
      PROFILES.USUARIO_TELEMEDIDA,
    ],
  },
  {
    id: 'dispositivos-no-asignados',
    icon: 'fa-unlink',
    labelKey: 'chatbot.map.dispositivos-no-asignados.label',
    descKey:  'chatbot.map.dispositivos-no-asignados.desc',
    payload:  'dispositivos/listado/no-asignados',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: [
      ...OPERATORS,
      PROFILES.ADMIN_INSTALLATION,
      PROFILES.TELEMEDIDA,
      PROFILES.USUARIO_TELEMEDIDA,
    ],
  },
  {
    id: 'dispositivos-desactivados',
    icon: 'fa-ban',
    labelKey: 'chatbot.map.dispositivos-desactivados.label',
    descKey:  'chatbot.map.dispositivos-desactivados.desc',
    payload:  'dispositivos/listado/desactivados',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: [
      ...OPERATORS,
      PROFILES.TELEMEDIDA,
      PROFILES.USUARIO_TELEMEDIDA,
    ],
  },
  {
    id: 'dispositivos-cliente',
    icon: 'fa-user-tie',
    labelKey: 'chatbot.map.dispositivos-cliente.label',
    descKey:  'chatbot.map.dispositivos-cliente.desc',
    payload:  'dispositivos/listado/cliente',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: CLIENT_ADMIN_UP,
  },
  {
    id: 'dispositivos-revision',
    icon: 'fa-sticky-note',
    labelKey: 'chatbot.map.dispositivos-revision.label',
    descKey:  'chatbot.map.dispositivos-revision.desc',
    payload:  'dispositivos/listado/revision',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'dispositivos-sustituciones',
    icon: 'fa-exchange-alt',
    labelKey: 'chatbot.map.dispositivos-sustituciones.label',
    descKey:  'chatbot.map.dispositivos-sustituciones.desc',
    payload:  'dispositivos/listado/sustituciones',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: [
      ...ENTITY_ADMIN_UP,
      PROFILES.ADMIN_AGRUPACION,
      PROFILES.ADMIN_INSTALLATION,
    ],
  },
  {
    id: 'dispositivos-sustituciones-pendientes',
    icon: 'fa-exchange-alt',
    labelKey: 'chatbot.map.dispositivos-sustituciones-pendientes.label',
    descKey:  'chatbot.map.dispositivos-sustituciones-pendientes.desc',
    payload:  'dispositivos/listado/sustituciones-pendientes',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: [
      ...ENTITY_ADMIN_UP,
      PROFILES.ADMIN_AGRUPACION,
      PROFILES.ADMIN_INSTALLATION,
    ],
  },
  {
    id: 'dispositivos-sin-comunicacion',
    icon: 'fa-exclamation-triangle',
    labelKey: 'chatbot.map.dispositivos-sin-comunicacion.label',
    descKey:  'chatbot.map.dispositivos-sin-comunicacion.desc',
    payload:  'dispositivos/listado/sin-comunicacion',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: [
      ...OPERATORS,
      PROFILES.TELEMEDIDA,
      PROFILES.USUARIO_TELEMEDIDA,
    ],
  },
  {
    id: 'dispositivos-error-activacion',
    icon: 'fa-exclamation',
    labelKey: 'chatbot.map.dispositivos-error-activacion.label',
    descKey:  'chatbot.map.dispositivos-error-activacion.desc',
    payload:  'dispositivos/listado/error-activacion',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: ARSON_ONLY,
  },
  {
    id: 'dispositivos-bateria-baja',
    icon: 'fa-battery-quarter',
    labelKey: 'chatbot.map.dispositivos-bateria-baja.label',
    descKey:  'chatbot.map.dispositivos-bateria-baja.desc',
    payload:  'dispositivos/listado/bateria-baja',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: OPERATORS,
  },
  {
    id: 'dispositivos-interno',
    icon: 'fa-network-wired',
    labelKey: 'chatbot.map.dispositivos-interno.label',
    descKey:  'chatbot.map.dispositivos-interno.desc',
    payload:  'dispositivos/listado/interno',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: ARSON_ONLY,
  },
  {
    id: 'dispositivos-gprs',
    icon: 'fa-wifi',
    labelKey: 'chatbot.map.dispositivos-gprs.label',
    descKey:  'chatbot.map.dispositivos-gprs.desc',
    payload:  'dispositivos/listado/interno-gprs',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: ARSON_ONLY,
  },
  {
    id: 'dispositivos-walk-by',
    icon: 'fa-road',
    labelKey: 'chatbot.map.dispositivos-walk-by.label',
    descKey:  'chatbot.map.dispositivos-walk-by.desc',
    payload:  'dispositivos/listado/walk-by',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: WITH_TELEMEDIDA,
  },
  {
    id: 'dispositivos-mbus',
    icon: 'fa-question-circle',
    labelKey: 'chatbot.map.dispositivos-mbus.label',
    descKey:  'chatbot.map.dispositivos-mbus.desc',
    payload:  'dispositivos/listado/desconocidos-mbus',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: ARSON_ONLY,
  },
  {
    id: 'dispositivos-ignorados',
    icon: 'fa-eye-slash',
    labelKey: 'chatbot.map.dispositivos-ignorados.label',
    descKey:  'chatbot.map.dispositivos-ignorados.desc',
    payload:  'dispositivos/listado/dispositivos-ignorados',
    group:    'devices',
    parentId: 'dispositivos',
    profiles: ARSON_ONLY,
  },
  {
    id: 'dispositivos-nuevo',
    icon: 'fa-plus-circle',
    labelKey: 'chatbot.map.dispositivos-nuevo.label',
    descKey:  'chatbot.map.dispositivos-nuevo.desc',
    payload:  'dispositivos/nuevo',
    group:    'devices',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'dispositivos-sim',
    icon: 'fa-sim-card',
    labelKey: 'chatbot.map.dispositivos-sim.label',
    descKey:  'chatbot.map.dispositivos-sim.desc',
    payload:  'dispositivos/sim',
    group:    'devices',
    profiles: ARSON_ONLY,
  },

  // ── Device operations (imports & M-Bus) ────────────────────────────────────
  {
    id: 'dispositivos-importaciones',
    icon: 'fa-file-upload',
    labelKey: 'chatbot.map.dispositivos-importaciones.label',
    descKey:  'chatbot.map.dispositivos-importaciones.desc',
    payload:  '',
    group:    'devices-ops',
    navigable: false,
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'dispositivos-importar-datos',
    icon: 'fa-tachometer-alt',
    labelKey: 'chatbot.map.dispositivos-importar-datos.label',
    descKey:  'chatbot.map.dispositivos-importar-datos.desc',
    payload:  'dispositivos/importar/datos',
    group:    'devices-ops',
    parentId: 'dispositivos-importaciones',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'dispositivos-importar-lecturas',
    icon: 'fa-calendar-alt',
    labelKey: 'chatbot.map.dispositivos-importar-lecturas.label',
    descKey:  'chatbot.map.dispositivos-importar-lecturas.desc',
    payload:  'dispositivos/importar/lecturas',
    group:    'devices-ops',
    parentId: 'dispositivos-importaciones',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'dispositivos-importar-texto',
    icon: 'fa-heading',
    labelKey: 'chatbot.map.dispositivos-importar-texto.label',
    descKey:  'chatbot.map.dispositivos-importar-texto.desc',
    payload:  'dispositivos/importar/texto',
    group:    'devices-ops',
    parentId: 'dispositivos-importaciones',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'dispositivos-importar-imagenes',
    icon: 'fa-image',
    labelKey: 'chatbot.map.dispositivos-importar-imagenes.label',
    descKey:  'chatbot.map.dispositivos-importar-imagenes.desc',
    payload:  'dispositivos/importar/imagenes',
    group:    'devices-ops',
    parentId: 'dispositivos-importaciones',
    profiles: ARSON_ONLY,
  },
  {
    id: 'dispositivos-mbus-fabricantes',
    icon: 'fa-industry',
    labelKey: 'chatbot.map.dispositivos-mbus-fabricantes.label',
    descKey:  'chatbot.map.dispositivos-mbus-fabricantes.desc',
    payload:  '',
    group:    'devices-ops',
    navigable: false,
    profiles: ARSON_ONLY,
  },
  {
    id: 'dispositivos-mbus-clave-comun',
    icon: 'fa-key',
    labelKey: 'chatbot.map.dispositivos-mbus-clave-comun.label',
    descKey:  'chatbot.map.dispositivos-mbus-clave-comun.desc',
    payload:  'dispositivos/fabricantes-mbus/clave-comun',
    group:    'devices-ops',
    parentId: 'dispositivos-mbus-fabricantes',
    profiles: ARSON_ONLY,
  },
  {
    id: 'dispositivos-mbus-clave-primaria',
    icon: 'fa-lock',
    labelKey: 'chatbot.map.dispositivos-mbus-clave-primaria.label',
    descKey:  'chatbot.map.dispositivos-mbus-clave-primaria.desc',
    payload:  'dispositivos/fabricantes-mbus/clave-primaria',
    group:    'devices-ops',
    parentId: 'dispositivos-mbus-fabricantes',
    profiles: ARSON_ONLY,
  },

  // ── Gateways ───────────────────────────────────────────────────────────────
  {
    id: 'gateways',
    icon: 'fa-broadcast-tower',
    labelKey: 'chatbot.map.gateways.label',
    descKey:  'chatbot.map.gateways.desc',
    payload:  'gateways/listado/asignados',
    group:    'gateways',
    profiles: ARSON_ONLY,
  },
  {
    id: 'gateways-disponibles',
    icon: 'fa-check-circle',
    labelKey: 'chatbot.map.gateways-disponibles.label',
    descKey:  'chatbot.map.gateways-disponibles.desc',
    payload:  'gateways/listado/disponibles',
    group:    'gateways',
    parentId: 'gateways',
    profiles: ARSON_ONLY,
  },
  {
    id: 'gateways-en-stock',
    icon: 'fa-box',
    labelKey: 'chatbot.map.gateways-en-stock.label',
    descKey:  'chatbot.map.gateways-en-stock.desc',
    payload:  'gateways/listado/en-stock',
    group:    'gateways',
    parentId: 'gateways',
    profiles: ARSON_ONLY,
  },
  {
    id: 'gateways-averiados',
    icon: 'fa-tools',
    labelKey: 'chatbot.map.gateways-averiados.label',
    descKey:  'chatbot.map.gateways-averiados.desc',
    payload:  'gateways/listado/averiados',
    group:    'gateways',
    parentId: 'gateways',
    profiles: ARSON_ONLY,
  },
  {
    id: 'gateways-reparacion',
    icon: 'fa-wrench',
    labelKey: 'chatbot.map.gateways-reparacion.label',
    descKey:  'chatbot.map.gateways-reparacion.desc',
    payload:  'gateways/listado/reparacion',
    group:    'gateways',
    parentId: 'gateways',
    profiles: ARSON_ONLY,
  },
  {
    id: 'gateways-appeui',
    icon: 'fa-key',
    labelKey: 'chatbot.map.gateways-appeui.label',
    descKey:  'chatbot.map.gateways-appeui.desc',
    payload:  'gateways/appeui',
    group:    'gateways',
    parentId: 'gateways',
    profiles: ARSON_ONLY,
  },

  // ── Work orders ────────────────────────────────────────────────────────────
  {
    id: 'ordenes',
    icon: 'fa-clipboard-list',
    labelKey: 'chatbot.map.ordenes.label',
    descKey:  'chatbot.map.ordenes.desc',
    payload:  'ordenes/listado',
    group:    'orders',
    profiles: [...WITH_TELEMEDIDA, PROFILES.ADMIN_INSTALLATION],
  },
  {
    id: 'ordenes-rutas',
    icon: 'fa-route',
    labelKey: 'chatbot.map.ordenes-rutas.label',
    descKey:  'chatbot.map.ordenes-rutas.desc',
    payload:  'ordenes/rutas',
    group:    'orders',
    parentId: 'ordenes',
    profiles: [...WITH_TELEMEDIDA, PROFILES.ADMIN_INSTALLATION],
  },

  // ── Data analysis ──────────────────────────────────────────────────────────
  {
    id: 'analisis',
    icon: 'fa-chart-line',
    labelKey: 'chatbot.map.analisis.label',
    descKey:  'chatbot.map.analisis.desc',
    payload:  'analisis-datos/grafica',
    group:    'data-analysis',
    profiles: [...OPERATORS, PROFILES.USUARIO_TELEMEDIDA],
  },
  {
    id: 'analisis-balance',
    icon: 'fa-balance-scale',
    labelKey: 'chatbot.map.analisis-balance.label',
    descKey:  'chatbot.map.analisis-balance.desc',
    payload:  'analisis-datos/balance',
    group:    'data-analysis',
    parentId: 'analisis',
    profiles: [...OPERATORS, ...TELEMEDIDA_ONLY],
  },
  {
    id: 'analisis-evolucion',
    icon: 'fa-chart-area',
    labelKey: 'chatbot.map.analisis-evolucion.label',
    descKey:  'chatbot.map.analisis-evolucion.desc',
    payload:  'analisis-datos/evolucion-consumo',
    group:    'data-analysis',
    parentId: 'analisis',
    profiles: [...OPERATORS, ...TELEMEDIDA_ONLY],
  },
  {
    id: 'analisis-comparativa',
    icon: 'fa-columns',
    labelKey: 'chatbot.map.analisis-comparativa.label',
    descKey:  'chatbot.map.analisis-comparativa.desc',
    payload:  'analisis-datos/comparativa-graficas',
    group:    'data-analysis',
    parentId: 'analisis',
    profiles: [...OPERATORS, ...TELEMEDIDA_ONLY],
  },
  {
    id: 'analisis-consultas',
    icon: 'fa-search',
    labelKey: 'chatbot.map.analisis-consultas.label',
    descKey:  'chatbot.map.analisis-consultas.desc',
    payload:  'analisis-datos/consultas',
    group:    'data-analysis',
    parentId: 'analisis',
    profiles: [...OPERATORS, ...TELEMEDIDA_ONLY],
  },
  {
    id: 'analisis-avanzada',
    icon: 'fa-brain',
    labelKey: 'chatbot.map.analisis-avanzada.label',
    descKey:  'chatbot.map.analisis-avanzada.desc',
    payload:  'analisis-datos/analitica-avanzada',
    group:    'data-analysis',
    parentId: 'analisis',
    profiles: [...OPERATORS, ...TELEMEDIDA_ONLY],
  },
  {
    id: 'analisis-asociaciones',
    icon: 'fa-link',
    labelKey: 'chatbot.map.analisis-asociaciones.label',
    descKey:  'chatbot.map.analisis-asociaciones.desc',
    payload:  'analisis-datos/asociaciones',
    group:    'data-analysis',
    parentId: 'analisis',
    profiles: [...OPERATORS, ...TELEMEDIDA_ONLY],
  },

  // ── Data management ────────────────────────────────────────────────────────
  {
    id: 'gestion-datos',
    icon: 'fa-database',
    labelKey: 'chatbot.map.gestion-datos.label',
    descKey:  'chatbot.map.gestion-datos.desc',
    payload:  'gestion-datos/informacion',
    group:    'data-mgmt',
    profiles: [...OPERATORS, PROFILES.USUARIO_TELEMEDIDA],
  },
  {
    id: 'cups',
    icon: 'fa-tint',
    labelKey: 'chatbot.map.cups.label',
    descKey:  'chatbot.map.cups.desc',
    payload:  'gestion-datos/cups/listado',
    group:    'data-mgmt',
    parentId: 'gestion-datos',
    profiles: OPERATORS,
  },
  {
    id: 'gestion-datos-cups-instalados',
    icon: 'fa-check-square',
    labelKey: 'chatbot.map.gestion-datos-cups-instalados.label',
    descKey:  'chatbot.map.gestion-datos-cups-instalados.desc',
    payload:  'gestion-datos/cups/listado-instalados',
    group:    'data-mgmt',
    parentId: 'gestion-datos',
    profiles: OPERATORS,
  },
  {
    id: 'gestion-datos-sin-cups',
    icon: 'fa-unlink',
    labelKey: 'chatbot.map.gestion-datos-sin-cups.label',
    descKey:  'chatbot.map.gestion-datos-sin-cups.desc',
    payload:  'gestion-datos/cups/dispositivos-sin-cups',
    group:    'data-mgmt',
    parentId: 'gestion-datos',
    profiles: OPERATORS,
  },
  {
    id: 'gestion-datos-configuracion',
    icon: 'fa-sliders-h',
    labelKey: 'chatbot.map.gestion-datos-configuracion.label',
    descKey:  'chatbot.map.gestion-datos-configuracion.desc',
    payload:  'gestion-datos/configuracion',
    group:    'data-mgmt',
    parentId: 'gestion-datos',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'gestion-datos-metrologia',
    icon: 'fa-ruler-combined',
    labelKey: 'chatbot.map.gestion-datos-metrologia.label',
    descKey:  'chatbot.map.gestion-datos-metrologia.desc',
    payload:  'gestion-datos/metrologia/configuracion',
    group:    'data-mgmt',
    parentId: 'gestion-datos',
    profiles: OPERATORS,
    requiresGas: true,
  },
  {
    id: 'gestion-datos-importacion',
    icon: 'fa-file-import',
    labelKey: 'chatbot.map.gestion-datos-importacion.label',
    descKey:  'chatbot.map.gestion-datos-importacion.desc',
    payload:  'gestion-datos/importar/datos',
    group:    'data-mgmt',
    parentId: 'gestion-datos',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'gestion-datos-pta',
    icon: 'fa-tachometer-alt',
    labelKey: 'chatbot.map.gestion-datos-pta.label',
    descKey:  'chatbot.map.gestion-datos-pta.desc',
    payload:  'gestion-datos/pta',
    group:    'data-mgmt',
    parentId: 'gestion-datos',
    profiles: ARSON_ONLY,
    clientIds: [PROD_CLIENT_LIST.NORTEGAS],
  },

  // ── Files ──────────────────────────────────────────────────────────────────
  {
    id: 'ficheros',
    icon: 'fa-file-export',
    labelKey: 'chatbot.map.ficheros.label',
    descKey:  'chatbot.map.ficheros.desc',
    payload:  'ficheros/descarga',
    group:    'files',
    profiles: OPERATORS,
  },
  {
    id: 'ficheros-plantillas',
    icon: 'fa-file-alt',
    labelKey: 'chatbot.map.ficheros-plantillas.label',
    descKey:  'chatbot.map.ficheros-plantillas.desc',
    payload:  'ficheros/plantillas/listado',
    group:    'files',
    parentId: 'ficheros',
    profiles: ARSON_ONLY,
  },
  {
    id: 'ficheros-horarios',
    icon: 'fa-clock',
    labelKey: 'chatbot.map.ficheros-horarios.label',
    descKey:  'chatbot.map.ficheros-horarios.desc',
    payload:  'ficheros/horarios',
    group:    'files',
    parentId: 'ficheros',
    profiles: ARSON_ONLY,
  },
  {
    id: 'ficheros-ek280',
    icon: 'fa-file-code',
    labelKey: 'chatbot.map.ficheros-ek280.label',
    descKey:  'chatbot.map.ficheros-ek280.desc',
    payload:  'ficheros/ek280',
    group:    'files',
    parentId: 'ficheros',
    profiles: ARSON_ONLY,
  },
  {
    id: 'ficheros-aqualia',
    icon: 'fa-water',
    labelKey: 'chatbot.map.ficheros-aqualia.label',
    descKey:  'chatbot.map.ficheros-aqualia.desc',
    payload:  'ficheros/aqualia',
    group:    'files',
    parentId: 'ficheros',
    profiles: ARSON_ONLY,
  },

  // ── Sensory ────────────────────────────────────────────────────────────────
  {
    id: 'sensorica',
    icon: 'fa-wave-square',
    labelKey: 'chatbot.map.sensorica.label',
    descKey:  'chatbot.map.sensorica.desc',
    payload:  'sensorica/fugas',
    group:    'sensory',
    profiles: [...OPERATORS, PROFILES.USUARIO_TELEMEDIDA],
  },
  {
    id: 'sensorica-presion',
    icon: 'fa-compress-arrows-alt',
    labelKey: 'chatbot.map.sensorica-presion.label',
    descKey:  'chatbot.map.sensorica-presion.desc',
    payload:  'sensorica/presion',
    group:    'sensory',
    parentId: 'sensorica',
    profiles: [...OPERATORS, ...TELEMEDIDA_ONLY],
  },
  {
    id: 'sensorica-caudal',
    icon: 'fa-water',
    labelKey: 'chatbot.map.sensorica-caudal.label',
    descKey:  'chatbot.map.sensorica-caudal.desc',
    payload:  'sensorica/caudal',
    group:    'sensory',
    parentId: 'sensorica',
    profiles: [...OPERATORS, ...TELEMEDIDA_ONLY],
  },

  // ── Network ────────────────────────────────────────────────────────────────
  {
    id: 'cobertura',
    icon: 'fa-signal',
    labelKey: 'chatbot.map.cobertura.label',
    descKey:  'chatbot.map.cobertura.desc',
    payload:  'cobertura/test',
    group:    'network',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'mapa',
    icon: 'fa-map',
    labelKey: 'chatbot.map.mapa.label',
    descKey:  'chatbot.map.mapa.desc',
    payload:  'cobertura/mapa',
    group:    'network',
    parentId: 'cobertura',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'cobertura-mbus',
    icon: 'fa-broadcast-tower',
    labelKey: 'chatbot.map.cobertura-mbus.label',
    descKey:  'chatbot.map.cobertura-mbus.desc',
    payload:  'cobertura/mapa-mbus',
    group:    'network',
    parentId: 'cobertura',
    profiles: ARSON_ONLY,
  },
  {
    id: 'estado-red',
    icon: 'fa-network-wired',
    labelKey: 'chatbot.map.estado-red.label',
    descKey:  'chatbot.map.estado-red.desc',
    payload:  'estado-red/agrupacion',
    group:    'network',
    profiles: [...ENTITY_ADMIN_UP, PROFILES.ADMIN_AGRUPACION],
  },
  {
    id: 'estado-red-entidad',
    icon: 'fa-city',
    labelKey: 'chatbot.map.estado-red-entidad.label',
    descKey:  'chatbot.map.estado-red-entidad.desc',
    payload:  'estado-red/entidad',
    group:    'network',
    parentId: 'estado-red',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'estado-red-kpis',
    icon: 'fa-chart-line',
    labelKey: 'chatbot.map.estado-red-kpis.label',
    descKey:  'chatbot.map.estado-red-kpis.desc',
    payload:  'estado-red/kpis',
    group:    'network',
    parentId: 'estado-red',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'estado-red-global',
    icon: 'fa-globe',
    labelKey: 'chatbot.map.estado-red-global.label',
    descKey:  'chatbot.map.estado-red-global.desc',
    payload:  'estado-red/global',
    group:    'network',
    parentId: 'estado-red',
    profiles: ARSON_ONLY,
  },
  {
    id: 'estado-red-resumen-global',
    icon: 'fa-tachometer-alt',
    labelKey: 'chatbot.map.estado-red-resumen-global.label',
    descKey:  'chatbot.map.estado-red-resumen-global.desc',
    payload:  'estado-red/resumen-global',
    group:    'network',
    parentId: 'estado-red',
    profiles: ARSON_ONLY,
  },

  // ── Maintenance — global ───────────────────────────────────────────────────
  {
    id: 'mantenimiento-busqueda',
    icon: 'fa-search',
    labelKey: 'chatbot.map.mantenimiento-busqueda.label',
    descKey:  'chatbot.map.mantenimiento-busqueda.desc',
    payload:  'mantenimiento/global/busqueda-global',
    group:    'maint-global',
    profiles: [...OPERATORS, PROFILES.TELEMEDIDA, PROFILES.USUARIO_TELEMEDIDA],
  },
  {
    id: 'mantenimiento-activaciones',
    icon: 'fa-plug',
    labelKey: 'chatbot.map.mantenimiento-activaciones.label',
    descKey:  'chatbot.map.mantenimiento-activaciones.desc',
    payload:  'mantenimiento/global/activaciones',
    group:    'maint-global',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'mantenimiento-estado-comunicaciones',
    icon: 'fa-satellite-dish',
    labelKey: 'chatbot.map.mantenimiento-estado-comunicaciones.label',
    descKey:  'chatbot.map.mantenimiento-estado-comunicaciones.desc',
    payload:  'mantenimiento/estado-comunicaciones',
    group:    'maint-global',
    profiles: [...ENTITY_ADMIN_UP, PROFILES.USUARIO_ARSON],
  },
  {
    id: 'mantenimiento-alarmas-globales',
    icon: 'fa-bell',
    labelKey: 'chatbot.map.mantenimiento-alarmas-globales.label',
    descKey:  'chatbot.map.mantenimiento-alarmas-globales.desc',
    payload:  'mantenimiento/global/alarmas',
    group:    'maint-global',
    profiles: OPERATORS,
  },
  {
    id: 'mantenimiento-concentradores',
    icon: 'fa-hdd',
    labelKey: 'chatbot.map.mantenimiento-concentradores.label',
    descKey:  'chatbot.map.mantenimiento-concentradores.desc',
    payload:  'mantenimiento/global/concentradores',
    group:    'maint-global',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-bateria',
    icon: 'fa-battery-quarter',
    labelKey: 'chatbot.map.mantenimiento-bateria.label',
    descKey:  'chatbot.map.mantenimiento-bateria.desc',
    payload:  'mantenimiento/global/bateria',
    group:    'maint-global',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'mantenimiento-cups-multiples',
    icon: 'fa-tint',
    labelKey: 'chatbot.map.mantenimiento-cups-multiples.label',
    descKey:  'chatbot.map.mantenimiento-cups-multiples.desc',
    payload:  'mantenimiento/global/cups-multiples-dispositivos',
    group:    'maint-global',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-incidencias',
    icon: 'fa-exclamation-circle',
    labelKey: 'chatbot.map.mantenimiento-incidencias.label',
    descKey:  'chatbot.map.mantenimiento-incidencias.desc',
    payload:  'mantenimiento/global/incidencias',
    group:    'maint-global',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-cola-comandos',
    icon: 'fa-terminal',
    labelKey: 'chatbot.map.mantenimiento-cola-comandos.label',
    descKey:  'chatbot.map.mantenimiento-cola-comandos.desc',
    payload:  'mantenimiento/global/cola-comandos',
    group:    'maint-global',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-usuarios-globales',
    icon: 'fa-users',
    labelKey: 'chatbot.map.mantenimiento-usuarios-globales.label',
    descKey:  'chatbot.map.mantenimiento-usuarios-globales.desc',
    payload:  'mantenimiento/global/usuarios',
    group:    'maint-global',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-usuarios-cliente',
    icon: 'fa-user-friends',
    labelKey: 'chatbot.map.mantenimiento-usuarios-cliente.label',
    descKey:  'chatbot.map.mantenimiento-usuarios-cliente.desc',
    payload:  'mantenimiento/global/usuarios-cliente',
    group:    'maint-global',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-eventos-usuario',
    icon: 'fa-list-alt',
    labelKey: 'chatbot.map.mantenimiento-eventos-usuario.label',
    descKey:  'chatbot.map.mantenimiento-eventos-usuario.desc',
    payload:  'mantenimiento/global/eventos-usuario',
    group:    'maint-global',
    profiles: OPERATORS,
  },
  {
    id: 'mantenimiento-reporte-comunicaciones',
    icon: 'fa-file-alt',
    labelKey: 'chatbot.map.mantenimiento-reporte-comunicaciones.label',
    descKey:  'chatbot.map.mantenimiento-reporte-comunicaciones.desc',
    payload:  'mantenimiento/global/reporte-comunicaciones',
    group:    'maint-global',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-lns',
    icon: 'fa-server',
    labelKey: 'chatbot.map.mantenimiento-lns.label',
    descKey:  'chatbot.map.mantenimiento-lns.desc',
    payload:  'mantenimiento/global/lns',
    group:    'maint-global',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-tareas',
    icon: 'fa-tasks',
    labelKey: 'chatbot.map.mantenimiento-tareas.label',
    descKey:  'chatbot.map.mantenimiento-tareas.desc',
    payload:  'mantenimiento/tareas-gateways',
    group:    'maint-global',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-registros',
    icon: 'fa-clipboard',
    labelKey: 'chatbot.map.mantenimiento-registros.label',
    descKey:  'chatbot.map.mantenimiento-registros.desc',
    payload:  'mantenimiento/registros',
    group:    'maint-global',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-construccion-red',
    icon: 'fa-sitemap',
    labelKey: 'chatbot.map.mantenimiento-construccion-red.label',
    descKey:  'chatbot.map.mantenimiento-construccion-red.desc',
    payload:  'mantenimiento/construccion-de-red',
    group:    'maint-global',
    profiles: ARSON_ONLY,
  },

  // ── Maintenance — entity ───────────────────────────────────────────────────
  {
    id: 'mantenimiento-entidad-gateways',
    icon: 'fa-broadcast-tower',
    labelKey: 'chatbot.map.mantenimiento-entidad-gateways.label',
    descKey:  'chatbot.map.mantenimiento-entidad-gateways.desc',
    payload:  'mantenimiento/entidad/gateways',
    group:    'maint-entity',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'mantenimiento-entidad-gateways-cliente',
    icon: 'fa-building',
    labelKey: 'chatbot.map.mantenimiento-entidad-gateways-cliente.label',
    descKey:  'chatbot.map.mantenimiento-entidad-gateways-cliente.desc',
    payload:  'mantenimiento/entidad/gateways-cliente',
    group:    'maint-entity',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'mantenimiento-entidad-concentradores',
    icon: 'fa-hdd',
    labelKey: 'chatbot.map.mantenimiento-entidad-concentradores.label',
    descKey:  'chatbot.map.mantenimiento-entidad-concentradores.desc',
    payload:  'mantenimiento/entidad/concentradores',
    group:    'maint-entity',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'mantenimiento-editar-agrupaciones',
    icon: 'fa-layer-group',
    labelKey: 'chatbot.map.mantenimiento-editar-agrupaciones.label',
    descKey:  'chatbot.map.mantenimiento-editar-agrupaciones.desc',
    payload:  'mantenimiento/entidad/editar-agrupaciones',
    group:    'maint-entity',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'mantenimiento-optimizar-red',
    icon: 'fa-rocket',
    labelKey: 'chatbot.map.mantenimiento-optimizar-red.label',
    descKey:  'chatbot.map.mantenimiento-optimizar-red.desc',
    payload:  'mantenimiento/entidad/optimizar-red',
    group:    'maint-entity',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-borrado-datos',
    icon: 'fa-eraser',
    labelKey: 'chatbot.map.mantenimiento-borrado-datos.label',
    descKey:  'chatbot.map.mantenimiento-borrado-datos.desc',
    payload:  'mantenimiento/entidad/borrado-datos',
    group:    'maint-entity',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-monitorizacion',
    icon: 'fa-heartbeat',
    labelKey: 'chatbot.map.mantenimiento-monitorizacion.label',
    descKey:  'chatbot.map.mantenimiento-monitorizacion.desc',
    payload:  'mantenimiento/monitorizacion/fiscalizador-mostrar',
    group:    'maint-entity',
    profiles: OPERATORS,
  },
  {
    id: 'mantenimiento-fabricantes',
    icon: 'fa-industry',
    labelKey: 'chatbot.map.mantenimiento-fabricantes.label',
    descKey:  'chatbot.map.mantenimiento-fabricantes.desc',
    payload:  'mantenimiento/configurar/fabricantes',
    group:    'maint-entity',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-cron',
    icon: 'fa-clock',
    labelKey: 'chatbot.map.mantenimiento-cron.label',
    descKey:  'chatbot.map.mantenimiento-cron.desc',
    payload:  'mantenimiento/configurar/cron',
    group:    'maint-entity',
    profiles: ARSON_ONLY,
  },
  {
    id: 'mantenimiento-ip',
    icon: 'fa-network-wired',
    labelKey: 'chatbot.map.mantenimiento-ip.label',
    descKey:  'chatbot.map.mantenimiento-ip.desc',
    payload:  'mantenimiento/configurar/ip',
    group:    'maint-entity',
    profiles: ARSON_ONLY,
  },

  // ── Administration ─────────────────────────────────────────────────────────
  {
    id: 'clientes',
    icon: 'fa-building',
    labelKey: 'chatbot.map.clientes.label',
    descKey:  'chatbot.map.clientes.desc',
    payload:  'clientes/listado',
    group:    'admin',
    profiles: ARSON_ONLY,
  },
  {
    id: 'entidades',
    icon: 'fa-city',
    labelKey: 'chatbot.map.entidades.label',
    descKey:  'chatbot.map.entidades.desc',
    payload:  'entidades/listado',
    group:    'admin',
    parentId: 'clientes',
    profiles: ENTITY_ADMIN_UP,
  },
  {
    id: 'agrupaciones',
    icon: 'fa-layer-group',
    labelKey: 'chatbot.map.agrupaciones.label',
    descKey:  'chatbot.map.agrupaciones.desc',
    payload:  'agrupaciones/listado',
    group:    'admin',
    parentId: 'clientes',
    profiles: OPERATORS,
  },

  // ── Users ──────────────────────────────────────────────────────────────────
  {
    id: 'usuarios',
    icon: 'fa-users',
    labelKey: 'chatbot.map.usuarios.label',
    descKey:  'chatbot.map.usuarios.desc',
    payload:  'usuarios/listado',
    group:    'users',
    profiles: [
      ...OPERATORS,
      PROFILES.ADMIN_INSTALLATION,
      PROFILES.INSTALADOR,
      PROFILES.TELEMEDIDA,
    ],
  },
  {
    id: 'usuarios-log',
    icon: 'fa-history',
    labelKey: 'chatbot.map.usuarios-log.label',
    descKey:  'chatbot.map.usuarios-log.desc',
    payload:  'usuarios/log/global',
    group:    'users',
    parentId: 'usuarios',
    profiles: [
      ...OPERATORS,
      PROFILES.ADMIN_INSTALLATION,
      PROFILES.INSTALADOR,
      PROFILES.TELEMEDIDA,
    ],
  },
  {
    id: 'usuarios-log-agrupacion',
    icon: 'fa-history',
    labelKey: 'chatbot.map.usuarios-log-agrupacion.label',
    descKey:  'chatbot.map.usuarios-log-agrupacion.desc',
    payload:  'usuarios/log/agrupacion',
    group:    'users',
    parentId: 'usuarios',
    profiles: OPERATORS,
  },
  {
    id: 'usuarios-importar',
    icon: 'fa-file-import',
    labelKey: 'chatbot.map.usuarios-importar.label',
    descKey:  'chatbot.map.usuarios-importar.desc',
    payload:  'usuarios/importar',
    group:    'users',
    parentId: 'usuarios',
    profiles: ARSON_ONLY,
  },

  // ── Installation ───────────────────────────────────────────────────────────
  {
    id: 'instalador',
    icon: 'fa-toolbox',
    labelKey: 'chatbot.map.instalador.label',
    descKey:  'chatbot.map.instalador.desc',
    payload:  'instalador/activacion',
    group:    'install',
    profiles: [PROFILES.ARSON, PROFILES.USUARIO_ARSON, PROFILES.INSTALADOR],
  },
  {
    id: 'instalador-historico',
    icon: 'fa-history',
    labelKey: 'chatbot.map.instalador-historico.label',
    descKey:  'chatbot.map.instalador-historico.desc',
    payload:  'instalador/historico',
    group:    'install',
    parentId: 'instalador',
    profiles: [PROFILES.ARSON, PROFILES.USUARIO_ARSON, PROFILES.INSTALADOR],
  },

  // ── Subscriber ─────────────────────────────────────────────────────────────
  {
    id: 'abonado',
    icon: 'fa-user-circle',
    labelKey: 'chatbot.map.abonado.label',
    descKey:  'chatbot.map.abonado.desc',
    payload:  'abonado/principal',
    group:    'subscriber',
    profiles: SUBSCRIBER_ONLY,
  },
  {
    id: 'abonado-configuracion',
    icon: 'fa-user-cog',
    labelKey: 'chatbot.map.abonado-configuracion.label',
    descKey:  'chatbot.map.abonado-configuracion.desc',
    payload:  'abonado/configuracion',
    group:    'subscriber',
    parentId: 'abonado',
    profiles: SUBSCRIBER_ONLY,
  },
];
